import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, User, Clock, CheckCircle } from "lucide-react";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error("Error fetching course:", error);
      toast.error("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error("Please login to purchase");
      navigate("/login");
      return;
    }

    setPurchasing(true);

    try {
      const orderResponse = await axios.post(
        `${API}/payment/create-order`,
        {
          course_id: id,
          amount: course.price,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: "INR",
        name: "Safety Training Institute",
        description: course.title,
        order_id: orderResponse.data.id,
        handler: async function (response) {
          try {
            await axios.post(
              `${API}/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                course_id: id,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
            toast.success("Purchase successful! Course unlocked!");
            navigate("/dashboard");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
        },
        theme: {
          color: "#0f3e3e",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-16 h-16 border-4 border-[#0f3e3e] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <p>Course not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="course-detail-page">
      <Navbar />

      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <img
                src={course.banner}
                alt={course.title}
                className="w-full rounded-lg shadow-2xl"
                data-testid="course-detail-banner"
              />
            </div>

            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0f3e3e] mb-4" data-testid="course-detail-title">
                {course.title}
              </h1>
              <p className="text-lg leading-relaxed text-muted-foreground mb-6" data-testid="course-detail-description">
                {course.description}
              </p>

              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-5 h-5" />
                  <span>{course.instructor}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <BookOpen className="w-5 h-5" />
                  <span>{course.chapter_count} chapters</span>
                </div>
              </div>

              <div className="bg-[#f8f9fa] p-6 rounded-lg mb-6">
                <p className="text-4xl font-bold text-[#0f3e3e] mb-2" data-testid="course-detail-price">
                  ₹{course.price}
                </p>
                <Button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="w-full bg-[#0f3e3e] text-white hover:bg-[#0f3e3e]/90 h-12 font-medium mt-4"
                  data-testid="buy-now-button"
                >
                  {purchasing ? "Processing..." : "Buy Now"}
                </Button>
              </div>

              <Card className="border-[#0f3e3e]/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-[#0f3e3e] mb-4">Course Content</h3>
                  <div className="space-y-3">
                    {course.chapters && course.chapters.map((chapter, index) => (
                      <div
                        key={chapter.id}
                        className="flex items-center gap-3 p-3 bg-[#f8f9fa] rounded-md"
                        data-testid={`chapter-item-${chapter.id}`}
                      >
                        <CheckCircle className="w-5 h-5 text-[#0f3e3e]" />
                        <div className="flex-1">
                          <p className="font-medium text-[#0f3e3e]">{chapter.title}</p>
                          <p className="text-sm text-muted-foreground">{chapter.duration}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      <Footer/>
    </div>
  );
};

export default CourseDetailPage;
